import { useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ROUTES } from '#/config/routes';
import { createInspectionKind } from '#/api/imsMgmt/settings/inspectionKind/create';
import { fetchInspectionKindDetail } from '#/api/imsMgmt/settings/inspectionKind/fetchDetail';
import { replaceInspectionKind } from '#/api/imsMgmt/settings/inspectionKind/replace';
import { formatDateTimeOrDash } from '#/utils/date';
import { toast } from '#/utils/toast';
import {
  inspectionKindDetailQueryKey,
  inspectionKindListQueryKeyPrefix,
} from '#/utils/queryKeys';
import { Button } from '#/modules/commonComponents/button';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import FloatingSelect from '#/modules/commonComponents/form/select/FloatingSelect';
import { ENTITY_STATUS_ACTIVE_INACTIVE_OPTIONS } from '#/modules/commonComponents/table/entityStatus';
import useFormValidationSummary from '#/hooks/useFormValidationSummary';
import PageShell from '#/modules/infra/layout/PageShell';
import InspectionConditionItemsEditor from
  '#/modules/imsMgmt/settings/inspectionKind/components/InspectionConditionItemsEditor';
import {
  getInspectionKindApiErrorMessage,
  getInspectionKindFieldIssue,
  isInspectionKindReloadConflict,
} from '#/modules/imsMgmt/settings/inspectionKind/helper/apiError';
import { buildInspectionKindWritePayload } from
  '#/modules/imsMgmt/settings/inspectionKind/helper/buildInspectionKindWritePayload';
import { INSPECTION_KIND_FIELD_LABELS } from '#/modules/imsMgmt/settings/inspectionKind/helper/formValidationLabels';
import {
  collectKnownDataTypes,
  createEmptyInspectionItem,
  mapInspectionKindToForm,
} from '#/modules/imsMgmt/settings/inspectionKind/helper/mapInspectionKindForm';
import { inspectionKindFormSchema } from '#/modules/imsMgmt/settings/inspectionKind/schemas/inspectionKindSchema';

const CreateEditInspectionKindPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const queryClient = useQueryClient();
  const updatedAtRef = useRef(null);
  const {
    isSubmitAttempted,
    markSubmitAttempted,
    resetValidationSummary,
    customMessages,
    applyManualIssues,
    clearManualIssues,
  } = useFormValidationSummary();

  const detailQuery = useQuery({
    queryKey: inspectionKindDetailQueryKey(id),
    queryFn: () => fetchInspectionKindDetail(id),
    enabled: isEdit,
  });
  const knownDataTypes = useMemo(
    () => collectKnownDataTypes(detailQuery.data?.data),
    [detailQuery.data],
  );

  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(inspectionKindFormSchema),
    defaultValues: {
      code: '',
      name: '',
      status: 'ACTIVE',
      items: [createEmptyInspectionItem()],
    },
  });

  const saveMutation = useMutation({
    mutationFn: (values) => {
      const payload = buildInspectionKindWritePayload(values, {
        includeUpdatedAt: isEdit,
        updatedAt: updatedAtRef.current,
      });
      if (isEdit) {
        return replaceInspectionKind(id, payload);
      }
      return createInspectionKind(payload);
    },
    retry: false,
    onSuccess: async (result) => {
      toast.success(result?.message || (isEdit ? '已發布下一版' : '建立成功'));
      await queryClient.invalidateQueries({ queryKey: inspectionKindListQueryKeyPrefix() });
      const nextDetail = result?.data;
      const nextId = nextDetail?.id;
      if (!isEdit && nextId) {
        queryClient.setQueryData(inspectionKindDetailQueryKey(nextId), result);
        navigate(ROUTES.inspectionKindDetail(nextId));
        return;
      }
      if (nextDetail) {
        queryClient.setQueryData(inspectionKindDetailQueryKey(id), result);
        updatedAtRef.current = nextDetail.record?.updatedAt ?? null;
        resetValidationSummary();
        reset(mapInspectionKindToForm(nextDetail));
      }
    },
    onError: (error) => {
      const issue = getInspectionKindFieldIssue(error);
      if (issue) {
        applyManualIssues(setError, clearErrors, [issue], {
          formatSummaryMessage: (item) => item.message,
        });
      } else {
        markSubmitAttempted();
      }
      toast.error(getInspectionKindApiErrorMessage(error, '儲存失敗'));
      if (isEdit && isInspectionKindReloadConflict(error)) {
        detailQuery.refetch();
      }
      if (error?.response?.data?.code === 'INSPECTION_KIND_CODE_DUPLICATED') {
        queryClient.invalidateQueries({ queryKey: inspectionKindListQueryKeyPrefix() });
      }
    },
  });

  useEffect(() => {
    const detail = detailQuery.data?.data;
    if (!isEdit || !detail) {
      return;
    }
    updatedAtRef.current = detail.record?.updatedAt ?? null;
    resetValidationSummary();
    reset(mapInspectionKindToForm(detail));
  }, [detailQuery.data, isEdit, reset, resetValidationSummary]);

  function handleInvalidSubmit() {
    markSubmitAttempted();
  }

  function onSubmit(values) {
    if (saveMutation.isPending) {
      return;
    }
    markSubmitAttempted();
    clearManualIssues(clearErrors);
    saveMutation.mutate(values);
  }

  function handleCodeBlur() {
    const next = String(getValues('code') ?? '').trim().toUpperCase();
    setValue('code', next, { shouldDirty: true, shouldValidate: true });
  }

  const pageTitle = isEdit ? '編輯檢測種類' : '新增檢測種類';
  const detail = detailQuery.data?.data;
  const isPageLoading = isEdit && detailQuery.isLoading;
  const isDetailNotFound = isEdit && detailQuery.isError;

  return (
    <PageShell variant="form">
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-4xl">
          <div className="create_edit_page__header">
            <h1 className="page_title mb-0!">
              <span className="border-l-5 border-primary-400 pl-2.5">{pageTitle}</span>
            </h1>
            <Button type="button" variant="outline" asChild>
              <Link to={isEdit ? ROUTES.inspectionKindDetail(id) : ROUTES.inspectionKind}>
                {isEdit ? '返回詳情' : '返回列表'}
              </Link>
            </Button>
          </div>

          {isPageLoading ? (
            <p className="text-sm text-muted-foreground">載入中…</p>
          ) : isDetailNotFound ? (
            <p className="text-sm text-destructive">
              {getInspectionKindApiErrorMessage(detailQuery.error, '找不到檢測種類')}
            </p>
          ) : (
            <>
              {isEdit && detail ? (
                <p className="mb-3 text-sm text-muted-foreground">
                  {`目前第 ${detail.revisionNo ?? '—'} 版，生效時間 ${
                    formatDateTimeOrDash(detail.revision?.effectiveFrom)
                  }。`}
                  生效時間由系統產生，儲存後會發布下一版。
                </p>
              ) : null}
              <FormValidationSummary
                show={isSubmitAttempted}
                errors={errors}
                fieldLabels={INSPECTION_KIND_FIELD_LABELS}
                customMessages={customMessages}
              />
              <form
                className="sheet create_edit create_edit_form"
                onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)}
                noValidate
              >
                <div className="create_edit_form__body">
                  <FloatingInput
                    id="code"
                    label="種類代碼"
                    register={register}
                    errors={errors}
                    readOnly={isEdit}
                    onBlur={handleCodeBlur}
                  />
                  <FloatingInput
                    id="name"
                    label="名稱"
                    register={register}
                    errors={errors}
                  />
                  <FloatingSelect
                    id="status"
                    label="狀態"
                    options={ENTITY_STATUS_ACTIVE_INACTIVE_OPTIONS}
                    control={control}
                    errors={errors}
                  />
                  <InspectionConditionItemsEditor
                    control={control}
                    register={register}
                    errors={errors}
                    setValue={setValue}
                    getValues={getValues}
                    knownDataTypes={knownDataTypes}
                  />
                </div>
                <div className="create_edit_form__actions flex flex-wrap justify-end gap-2">
                  <Button type="button" variant="outline" asChild>
                    <Link to={isEdit ? ROUTES.inspectionKindDetail(id) : ROUTES.inspectionKind}>
                      取消
                    </Link>
                  </Button>
                  <Button type="submit" disabled={saveMutation.isPending}>
                    {saveMutation.isPending ? '儲存中…' : '儲存'}
                  </Button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </PageShell>
  );
};

export default CreateEditInspectionKindPage;
